import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FolderCopy as FolderOpenIcon,
  ChatBubbleOutline as ChatBubbleIcon,
  CreateNewFolder as CreateNewFolderIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import type { TreeNodeProps, DragItem } from "./types";

const DRAG_TYPE = "tree-node";

export const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  onMove,
  onRename,
  onDelete,
  onCreateFolder,
  level = 0,
  isRoot = false,
  selectedId,
  onSelect,
}) => {
  const isFirstLevel = isRoot;
  const [isExpanded, setIsExpanded] = useState(
    isFirstLevel || node.id === "today-section"
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [insertPosition, setInsertPosition] = useState<
    "before" | "after" | "inside" | null
  >(null);
  const [showDropIndicator, setShowDropIndicator] = useState(false);

  useEffect(() => {
    if (isFirstLevel) setIsExpanded(true);
  }, [isFirstLevel]);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { id: node.id, type: node.type, node },
    canDrag:
      !isRoot && node.id !== "today-section" && node.id !== "past-section",
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (item: DragItem, monitor) => {
      if (monitor.didDrop() || item.id === node.id) return;

      const clientOffset = monitor.getClientOffset();
      let insertAfter: boolean | undefined = undefined;

      if (clientOffset) {
        const rect = document
          .getElementById(`tree-item-${node.id}`)
          ?.getBoundingClientRect();
        if (rect) {
          const relativeY = clientOffset.y - rect.top;
          if (
            node.type === "folder" &&
            !isRoot &&
            node.id !== "root-folders" &&
            node.id !== "today-section" &&
            node.id !== "past-section"
          ) {
            const threshold = rect.height * 0.15;
            if (relativeY > threshold && relativeY < rect.height - threshold) {
              insertAfter = undefined;
            } else {
              insertAfter = relativeY > rect.height / 2;
            }
          } else {
            insertAfter = relativeY > rect.height / 2;
          }
        }
      }

      setInsertPosition(null);
      onMove(item.node, node.id, insertAfter);
    },
    canDrop: (item) => {
      if (item.id === node.id) return false;
      if (
        node.id === "root-conversations" ||
        node.id === "today-section" ||
        node.id === "past-section"
      )
        return false;
      if (node.id === "root-folders") return item.type === "folder";
      if (node.type === "folder" && !isRoot) return true;
      if (
        !isRoot &&
        node.id !== "root-folders" &&
        node.id !== "root-conversations"
      ) {
        return true;
      }
      return false;
    },
    collect: (monitor) => {
      if (monitor.isOver() && monitor.canDrop()) {
        setShowDropIndicator(true);
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          const rect = document
            .getElementById(`tree-item-${node.id}`)
            ?.getBoundingClientRect();
          if (rect) {
            const relativeY = clientOffset.y - rect.top;
            if (
              node.type === "folder" &&
              !isRoot &&
              node.id !== "root-folders" &&
              node.id !== "today-section" &&
              node.id !== "past-section"
            ) {
              const threshold = rect.height * 0.15;
              if (
                relativeY > threshold &&
                relativeY < rect.height - threshold
              ) {
                setInsertPosition("inside");
              } else {
                setInsertPosition(
                  relativeY > rect.height / 2 ? "after" : "before"
                );
              }
            } else {
              setInsertPosition(
                relativeY > rect.height / 2 ? "after" : "before"
              );
            }
          }
        }
      } else {
        setShowDropIndicator(false);
        setInsertPosition(null);
      }
      return {
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      };
    },
  });

  const dragDropRef = useCallback(
    (element: HTMLElement | null) => {
      if (!isRoot && node.id !== "today-section" && node.id !== "past-section")
        drag(element);
      if (
        node.id === "root-folders" ||
        (!isRoot && node.type === "folder") ||
        (!isRoot &&
          node.id !== "root-folders" &&
          node.id !== "root-conversations" &&
          node.id !== "today-section" &&
          node.id !== "past-section")
      ) {
        drop(element);
      }
    },
    [drag, drop, isRoot, node.id, node.type]
  );

  const handleRename = () => {
    onRename(node);
    setMenuAnchor(null);
  };

  const handleDelete = () => {
    onDelete(node);
    setMenuAnchor(null);
  };

  const handleCreateFolder = () => {
    if (onCreateFolder) {
      const parentId = node.id === "root-folders" ? undefined : node.id;
      onCreateFolder(parentId);
    }
    setMenuAnchor(null);
  };

  const indentLevel = level === 0 ? 0 : level === 1 ? 0 : (level - 1) * 2;

  return (
    <Box
      ref={dragDropRef}
      id={`tree-item-${node.id}`}
      sx={{ position: "relative", pl: `${indentLevel * 8}px` }}
    >
      {showDropIndicator &&
        insertPosition === "before" &&
        isOver &&
        canDrop &&
        !isRoot && (
          <Box
            sx={{
              position: "absolute",
              top: -2,
              left: 0,
              right: 0,
              height: "3px",
              backgroundColor: "primary.main",
              zIndex: 10,
              boxShadow: "0 0 8px rgba(255, 152, 0, 0.8)",
              borderRadius: "2px",
            }}
          />
        )}

      {showDropIndicator &&
        insertPosition === "after" &&
        isOver &&
        canDrop &&
        !isRoot && (
          <Box
            sx={{
              position: "absolute",
              bottom: -2,
              left: 0,
              right: 0,
              height: "3px",
              backgroundColor: "primary.main",
              zIndex: 10,
              boxShadow: "0 0 8px rgba(255, 152, 0, 0.8)",
              borderRadius: "2px",
            }}
          />
        )}

      <Box sx={{ opacity: isDragging ? 0.5 : 1 }}>
        <Tooltip title={node.label} placement="right" enterDelay={400}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 1,
              borderRadius: 3,
              backgroundColor:
                selectedId === node.id && !isRoot
                  ? "rgba(224, 224, 224, 0.95)"
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  selectedId === node.id && !isRoot
                    ? "rgba(224, 224, 224, 0.95)"
                    : "rgba(255, 255, 255, 0.1)",
              },
              cursor:
                node.type === "conversation" && !isRoot ? "pointer" : "default",
            }}
            onClick={() => {
              if (node.type === "folder" && !isRoot) {
                setIsExpanded(!isExpanded);
              } else if (node.type === "conversation" && !isRoot && onSelect) {
                onSelect(node);
              }
            }}
          >
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  width: "calc(100% - 32px)",
                }}
              >
                {node.type === "folder" && !isRoot && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    sx={{
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                      p: 0.25,
                      mr: 0.5,
                      transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 0.2s",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: "1.125rem" }} />
                  </IconButton>
                )}
                {node.id === "root-folders" && (
                  <FolderOpenIcon
                    sx={{
                      fontSize: "1.5rem",
                      mr: 1,
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                    }}
                  />
                )}
                {node.id === "root-conversations" && (
                  <QuestionAnswerIcon
                    sx={{
                      fontSize: "1.5rem",
                      mr: 1,
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                    }}
                  />
                )}
                {node.type === "conversation" && !isRoot && (
                  <ChatBubbleIcon
                    sx={{
                      fontSize: "1.125rem",
                      mr: 1,
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      selectedId === node.id && !isRoot
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(255, 255, 255, 0.7)",
                    fontSize: isRoot ? "0.95rem" : "0.875rem",
                    fontWeight: 400,
                    maxWidth: "100%",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    "&:hover": {
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "primary.main",
                    },
                  }}
                >
                  {node.label}
                </Typography>
              </Box>
              {(node.id === "root-folders" ||
                (!isRoot &&
                  node.id !== "today-section" &&
                  node.id !== "past-section")) &&
                !isDragging && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget);
                    }}
                    sx={{
                      color:
                        selectedId === node.id && !isRoot
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                      "&:hover": {
                        color: "primary.main",
                        backgroundColor: "rgba(255, 152, 0, 0.1)",
                      },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
            </>
          </Box>
        </Tooltip>

        {node.type === "folder" &&
          isExpanded &&
          node.children &&
          node.children.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {node.children.map((child) => (
                <TreeNode
                  key={child.id}
                  node={child}
                  onMove={onMove}
                  onRename={onRename}
                  onDelete={onDelete}
                  onCreateFolder={onCreateFolder}
                  level={level + 1}
                  isRoot={false}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {node.type === "folder" && (
          <MenuItem onClick={handleCreateFolder}>
            <ListItemIcon>
              <CreateNewFolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Crea nuova cartella</ListItemText>
          </MenuItem>
        )}
        {!isRoot && (
          <>
            <MenuItem onClick={handleRename}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Rinomina</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Elimina</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};
